import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHeartbeat, faCheckCircle, faExclamationTriangle, faDatabase } from '@fortawesome/free-solid-svg-icons';
import InternalShell, { useInternalApi } from '../layouts/InternalShell';
import { SUPER_ADMIN_NAV } from './nav';

/**
 * Reuses uzima_admins' real ops-health endpoint (tracker Phase 4.1) rather
 * than duplicating migration/DB-health introspection - this dashboard just
 * gives it a home behind the Super Admin nav instead of the standalone
 * /admin/dashboards/ops-health page it only had before.
 */

interface HealthData {
    database_connected: boolean;
    unapplied_migrations: string[];
    unapplied_migrations_count: number;
    recent_activity: { action: string; target_model: string; description: string; timestamp: string }[];
    counts: { active_collaborations: number; open_service_requests: number; active_clinics: number; active_staff: number };
}

const PlatformHealth: React.FC = () => {
    const { api } = useInternalApi();
    const [data, setData] = useState<HealthData | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => { document.title = 'Platform Health - Uzima Internal'; }, []);

    useEffect(() => {
        api.get('admin-dashboard/dashboards/ops-health/').then(res => {
            if (res.data?.status === 'success') setData(res.data.data);
        }).finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <InternalShell activeDashboard="super_admin" subNav={SUPER_ADMIN_NAV}>
            <h2 className="fw-bold mb-1"><FontAwesomeIcon icon={faHeartbeat} className="me-2 text-primary" />Platform Health</h2>
            <p className="text-muted mb-4">Live database, migration and activity status</p>

            {loading ? (
                <div className="text-center py-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>
            ) : !data ? (
                <div className="alert alert-danger">Could not load platform health.</div>
            ) : (
                <>
                    <div className="row mb-4">
                        <div className="col-md-3 mb-3">
                            <div className={`card border-0 shadow-sm ${data.database_connected ? '' : 'border-danger'}`}>
                                <div className="card-body">
                                    <div className="text-muted small mb-1">Database</div>
                                    <h4 className={data.database_connected ? 'text-success' : 'text-danger'}>
                                        <FontAwesomeIcon icon={data.database_connected ? faCheckCircle : faExclamationTriangle} className="me-2" />
                                        {data.database_connected ? 'Connected' : 'Unreachable'}
                                    </h4>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-3 mb-3">
                            <div className={`card border-0 shadow-sm ${data.unapplied_migrations_count > 0 ? 'border-warning' : ''}`}>
                                <div className="card-body">
                                    <div className="text-muted small mb-1">Unapplied Migrations</div>
                                    <h4 className={data.unapplied_migrations_count > 0 ? 'text-warning' : 'text-success'}>{data.unapplied_migrations_count}</h4>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-3 mb-3">
                            <div className="card border-0 shadow-sm">
                                <div className="card-body">
                                    <div className="text-muted small mb-1">Active Clinics</div>
                                    <h4>{data.counts.active_clinics}</h4>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-3 mb-3">
                            <div className="card border-0 shadow-sm">
                                <div className="card-body">
                                    <div className="text-muted small mb-1">Active Staff</div>
                                    <h4>{data.counts.active_staff}</h4>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="row mb-4">
                        <div className="col-md-6 mb-3">
                            <div className="card border-0 shadow-sm h-100">
                                <div className="card-body">
                                    <div className="text-muted small mb-1">Active Collaborations</div>
                                    <h4>{data.counts.active_collaborations}</h4>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-6 mb-3">
                            <div className="card border-0 shadow-sm h-100">
                                <div className="card-body">
                                    <div className="text-muted small mb-1">Open Service Requests</div>
                                    <h4>{data.counts.open_service_requests}</h4>
                                </div>
                            </div>
                        </div>
                    </div>

                    {data.unapplied_migrations_count > 0 && (
                        <div className="alert alert-warning">
                            <FontAwesomeIcon icon={faDatabase} className="me-2" />
                            <strong>Unapplied migrations:</strong> {data.unapplied_migrations.join(', ')}
                        </div>
                    )}

                    <div className="card border-0 shadow-sm">
                        <div className="card-header bg-white"><strong>Recent Admin Activity</strong></div>
                        <div className="card-body p-0">
                            {data.recent_activity.length === 0 ? (
                                <div className="text-center py-4 text-muted">No recent admin activity.</div>
                            ) : (
                                <table className="table table-hover align-middle mb-0">
                                    <thead className="table-light"><tr><th>Action</th><th>Target</th><th>Description</th><th>When</th></tr></thead>
                                    <tbody>
                                        {data.recent_activity.map((a, i) => (
                                            <tr key={i}>
                                                <td><span className="badge bg-secondary">{a.action}</span></td>
                                                <td>{a.target_model}</td>
                                                <td>{a.description}</td>
                                                <td>{new Date(a.timestamp).toLocaleString()}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                </>
            )}
        </InternalShell>
    );
};

export default PlatformHealth;
